import { useEffect, useMemo, useRef } from "react";

import { Lock } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";

interface VgsThreeDSModalProps {
  isOpen: boolean;
  challengeHtml: string;
  challengeUrl?: string;
  transactionId: string;
  onClose: (error?: string) => void;
  cartToken: string;
}

function escapeAttr(s: string | null): string {
  return (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Parse the challenge HTML to extract the form action URL and hidden fields.
 * This lets us POST directly to the bank ACS, bypassing intermediary pages
 * (e.g. Entersekt's red.js auto-submit) that cause extra iframe loads.
 */
function parseDirectPostHtml(
  challengeHtml: string,
  challengeUrl?: string,
): string | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(challengeHtml, "text/html");
    const form = doc.querySelector("form");
    if (!form) return null;

    const action = challengeUrl || form.getAttribute("action");
    if (!action) return null;

    const hiddenInputs = form.querySelectorAll('input[type="hidden"]');
    if (hiddenInputs.length === 0) return null;

    const fieldsHtml = Array.from(hiddenInputs)
      .map(
        (input) =>
          `<input type="hidden" name="${escapeAttr(input.getAttribute("name"))}" value="${escapeAttr(input.getAttribute("value"))}">`,
      )
      .join("\n");

    return `<!DOCTYPE html>
<html>
<body>
<form id="acsForm" method="POST" action="${escapeAttr(action)}">
${fieldsHtml}
</form>
<script>document.getElementById("acsForm").submit();</script>
</body>
</html>`;
  } catch {
    return null;
  }
}

/**
 * Pure iframe container for the 3DS challenge.
 *
 * The modal does NOT try to detect challenge completion via load-counting or
 * postMessage — that was fragile across different bank ACS implementations.
 * Instead, `useVgs3DSCompletion` subscribes to Pusher as soon as the modal
 * opens and receives the authoritative result from the backend.
 *
 * This modal only handles:
 * - Rendering the ACS challenge iframe
 * - User cancellation (Cancel button in header band)
 * - Hard timeout (6.5 min) as a safety net
 */
export default function VgsThreeDSModal({
  isOpen,
  challengeHtml,
  challengeUrl,
  transactionId: _transactionId,
  onClose,
  cartToken: _cartToken,
}: VgsThreeDSModalProps) {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onCloseRef = useRef(onClose);
  // Capture t in a ref so the 5-min timeout effect can read the current
  // translation without depending on t in its deps array (useTranslation
  // returns a new function reference per render and would otherwise reset
  // the timer on every re-render).
  const tRef = useRef(t);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    tRef.current = t;
  });

  // Build a direct-POST page that skips intermediary auto-redirect pages.
  // Falls back to the original challengeHtml if parsing fails.
  const iframeSrcDoc = useMemo(
    () => parseDirectPostHtml(challengeHtml, challengeUrl) ?? challengeHtml,
    [challengeHtml, challengeUrl],
  );

  // Hard timeout — aligned just past the backend safety-net boundary (~6:00)
  // so the cart poll surfaces the backend's verdict (accurate decline,
  // rescued approval, or TIMEOUT) before the modal gives up at 6.5 min.
  // (The old comment attributed the 5-minute value to the EMVCo spec;
  // EMVCo leaves challenge-timeout handling to the ACS/issuer — the real
  // constraint is our own safety-net boundary.) Depends only on isOpen so
  // the timer resets on open, not on content change.
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      onCloseRef.current(tRef.current("payment_verification_timed_out"));
    }, 390_000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="h-[600px] max-w-2xl p-0" hideClose>
        <div className="flex h-full w-full flex-col">
          {/* Header band — replaces VisuallyHidden DialogTitle */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-gray-600" aria-hidden="true" />
              <DialogTitle className="text-sm font-semibold text-gray-900">
                {t("verify_your_payment")}
              </DialogTitle>
            </div>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded text-xs text-indigo-600 hover:text-indigo-700 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              {t("cancel")}
            </button>
          </div>

          {/* Iframe (unchanged behavior). */}
          <div className="relative flex-1">
            {challengeHtml && (
              <iframe
                ref={iframeRef}
                srcDoc={iframeSrcDoc}
                className="h-full w-full border-0"
                title="VGS 3DS Challenge"
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups"
              />
            )}
          </div>

          {/* Footer band — indeterminate progress + status caption + tab-refresh hint */}
          <div className="border-t border-gray-200 px-4 py-3">
            <div
              data-testid="threeds-progress-bar"
              role="progressbar"
              aria-label={t("still_working_with_your_bank")}
              className="relative h-1 overflow-hidden rounded-full bg-indigo-100"
            >
              <div className="threeds-progress-pill absolute top-0 h-full w-[30%] rounded-full bg-indigo-500" />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-gray-600">
                {t("still_working_with_your_bank")}
              </span>
              <span className="text-gray-400">
                {t("dont_refresh_this_tab")}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
